/* eslint-disable react/require-default-props */
import React, { useEffect, useContext, useState, useMemo } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { BuyboxActions } from '@thd-olt-component-react/buybox/dist/component/subcomponents/BuyboxActions';
import { BuyboxMessage } from '@thd-olt-component-react/buybox/dist/component/subcomponents/BuyboxMessage';
import {
  params, string, arrayOf, bool, shape, extend
} from '@thd-nucleus/data-sources';
import { isPaypalEligible } from '@thd-olt-component-react/buybox/dist/BuyboxHelper';
import { Buybox } from '@thd-olt-component-react/buybox';
import BuyboxContainer from './subcomponents/BuyboxContainer';
import { MissingProductToaster } from './subcomponents/productUnavailable/MissingProductToaster';
import useProductsFullfilmentValidation from '../hooks/useProductsFullfilmentValidation';
import useGetCartItems from '../hooks/useGetCartItems';
import {
  bundleAddToCart, filterRemovedProducts, getB2BUser, getB2CUser
  , getBundleFulfillment, getPaypalCartItems, getProductsDefaultValue, isReturnMessagesEligible,
  getBundleQuantityLimit, hasMissingProducts, combineBundleProducts, expandAccessoryQuantities,
  getUpdatedBundleFulfillment
} from '../utils/product-bundle-utils';
import { BundleFulfillment } from './BundleFulfillment';
import { isBundleUnavailable, getExcludedShipStateProduct } from '../utils/product-unavailable-utils';
import '../styles/BundleBuybox.scss';
import { DeliveryInfo } from './subcomponents/DeliveryInfo';
import { BundleQuantityMessaging } from './subcomponents/BundleQuantityMessaging';
import { useGetDeliveryAndStoreZip } from '../hooks/useGetDeliveryAndStoreZip';
import useDeviceType from '../hooks/useDeviceType';

const {
  arrayOf: propTypeArrayOf, shape: shapeProp, string: propTypeString, number: propTypeNumber, bool: propTypeBool
} = proptypes;
/**
 * Props
 *
 * @typedef {object} Props
 * @property {Product[]} products Array of products
 * @property {String} itemId parent itemid
 */

/**
 * Returns a component, BundleSummary, showing the total price and
 * discounts applied to the bundle.
 * @param {Props} Props
 * @returns {React.ReactElement} JSX
 */

export const BundleBuybox = ({
  product, products, itemId, pricingDetails = null, selectedAccessories, bundleLoading = false
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-buybox.ready');
  }, []);

  const {
    quantity,
    paypalCheckout,
    addToCart,
    deliveryInfo,
    deliveryExpress,
    showQuantityLimit,
    displayMissingProductsToaster,
    disableATCForMissingProducts,
    hideBuybox,
    enableFulfillmentTiles,
    showEssMessage
  } = product?.features ?? {};

  const maxQuantityLimit = pricingDetails?.maxQuantityLimit;

  const isBipFulfillmentTilesEnabled = useConfigService('isBipFulfillmentTilesEnabled');
  const normalizedAccessories = expandAccessoryQuantities(selectedAccessories);
  const bundleProducts = combineBundleProducts(products, normalizedAccessories);
  const activeProducts = useMemo(() => filterRemovedProducts(bundleProducts), [products, selectedAccessories]);
  const { storeId, storeZip, deliveryZip } = useGetDeliveryAndStoreZip();
  const { isConsumerApp } = useDeviceType();
  const [closeMissingProductToaster, setCloseMissingProductToaster] = useState(false);
  const experienceContext = useContext(ExperienceContext);
  const [bundleQuantity, setBundleQuantity] = useState(1);
  const { experienceType, type } = product?.bundleSpecificationDetails || {};
  const isUnavailable = hideBuybox && isBundleUnavailable({ products });
  const { isExcluded } = useMemo(() =>
    getExcludedShipStateProduct({ products: activeProducts }), [activeProducts]);

  const {
    disableATC,
    productsFulfillmentInformation
  } = useProductsFullfilmentValidation({
    products: activeProducts,
    deliveryZip,
    bundleQuantity,
    storeZip,
    quantityLimitInventory: maxQuantityLimit?.quantityLimitWithInventory,
    addToCartFlag: addToCart,
    disableATCForMissingProductsFlag: disableATCForMissingProducts
  });

  const { cartItems } = useGetCartItems({
    productsFulfillmentInformation,
    bundleQuantity,
    storeId,
    deliveryZip,
    storeZip,
    bundleId: itemId,
    experienceType,
    bundleType: type
  });

  const onQuantityChange = (event) => setBundleQuantity(Number(event?.quantity));
  const bundleAtcClickPassThrough = () => bundleAddToCart({
    cartItems, storeId, storeZip, zipCode: deliveryZip, product
  });

  const isPaypalB2BEnabled = useConfigService('PAYPAL_ENABLE_B2B');
  const paypalEligible = isPaypalEligible(getB2BUser(), getB2CUser(), isPaypalB2BEnabled);
  const showPaypalSmartButton = useConfigService('PAYPAL_ENABLE');
  const updatedFulfillment = getUpdatedBundleFulfillment({
    bundleFulfillment: product?.fulfillment,
    products: activeProducts
  });

  const isRisMessageEnabled = useConfigService('fs:isRisMessageEnabled');
  const isReturnMessageEligible = isReturnMessagesEligible(activeProducts);
  const quantityLimit = getBundleQuantityLimit(maxQuantityLimit, showQuantityLimit);

  const { value } = getProductsDefaultValue(activeProducts);

  const fulfillmentMethod = cartItems?.[0]?.fulfillmentMethod;
  const isIncompleteBundle = hasMissingProducts(products);
  const onCloseMissingProductToaster = () => {
    setCloseMissingProductToaster(true);
  };
  const paypalCartItems = useMemo(() => {
    return getPaypalCartItems({ cartItems, deliveryZip });
  }, [cartItems]);

  const fulfillmentOptions = useMemo(() => {
    return getBundleFulfillment({ products: activeProducts });
  }, [activeProducts]);

  if (isUnavailable || (showEssMessage && isExcluded)) {
    return null;
  }

  const buyboxWrapperClass = 'buybox-wrapper' + (!quantity ? ' buybox-hide-quantity' : '');
  const bundleRisMessage = 'This bundle is only returnable in store.';
  const showBundleRisMessage = isRisMessageEnabled && isReturnMessageEligible;
  const hidePaypalCheckout = !paypalCheckout || !showPaypalSmartButton;
  const showMissingProductToaster = displayMissingProductsToaster && isIncompleteBundle && !closeMissingProductToaster;

  const isExchangeCustomer = experienceContext?.customer?.thd_storefront?.storeFrontType === 'EXCHANGE';
  const renderFulfillmentTiles = enableFulfillmentTiles
    && isBipFulfillmentTilesEnabled && !isExchangeCustomer;

  return (
    <BuyboxContainer>
      {!renderFulfillmentTiles && showBundleRisMessage && (
        <div data-testid="return-messaging">
          <BuyboxMessage message={bundleRisMessage} status="info" />
        </div>
      )}
      <div className={buyboxWrapperClass} data-testid="buybox-wrapper-buybox-actions">
        {!renderFulfillmentTiles && showQuantityLimit && (
          <BundleQuantityMessaging
            maxQuantity={maxQuantityLimit?.quantityLimit}
            quantity={bundleQuantity}
          />
        )}
        {renderFulfillmentTiles ? (
          <Buybox
            itemId={itemId}
            customRisMessage={showBundleRisMessage ? bundleRisMessage : ''}
            sticky={!isConsumerApp}
            hideInstantCheckout
            hidePaypalCheckout={hidePaypalCheckout}
            quantityLimitOverride={maxQuantityLimit?.quantityLimit}
            directData={{
              ...product,
              fulfillment: updatedFulfillment
            }}
            isDirectDataLoading={bundleLoading}
          />
        ) : (
          <BuyboxActions
            itemId={itemId}
            configurableItems={cartItems}
            onQuantityChange={onQuantityChange}
            quantity={bundleQuantity}
            onClickPassThrough={bundleAtcClickPassThrough}
            paypalEligible={paypalEligible}
            hideInstantCheckout
            sticky={!isConsumerApp}
            hideQuantity={!quantity}
            hidePaypalCheckout={hidePaypalCheckout}
            disableATC={disableATC}
            fulfillment={fulfillmentOptions}
            bipCartItems={paypalCartItems}
            itemPrice={value}
            fulfillmentMethod={fulfillmentMethod}
            quantityLimit={quantityLimit}
          />
        )}
        {showMissingProductToaster && (
          <MissingProductToaster onClose={onCloseMissingProductToaster} product={product} />
        )}
        {!renderFulfillmentTiles && (
          <div className="sui-mt-6">
            {
              deliveryInfo
                ? <DeliveryInfo deliveryExpress={deliveryExpress} />
                : <BundleFulfillment products={activeProducts} />
            }
          </div>
        )}
      </div>
    </BuyboxContainer>
  );
};

BundleBuybox.propTypes = {
  product: shapeProp({
    bundleSpecificationDetails: shapeProp({
      type: propTypeString
    })
  }).isRequired,
  products: propTypeArrayOf(
    shapeProp({
      fulfillment: shapeProp(),
      info: shapeProp()
    })
  ).isRequired,
  itemId: propTypeString.isRequired,
  pricingDetails: shapeProp({
    maxQuantityLimit: propTypeNumber
  }),
  selectedAccessories: propTypeArrayOf(shapeProp({})).isRequired,
  bundleLoading: propTypeBool
};

BundleBuybox.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      bundleSpecificationDetails: params({ storeId: string(), zipCode: string() }).shape({
        type: string()
      })
    })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      info: shape({
        quantityLimit: string(),
        isInStoreReturnMessageEligible: bool(),
        returnable: string()
      }),
      fulfillment: {
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        bodfsAssemblyEligible: bool(),
        seasonStatusEligible: bool(),
        excludedShipStates: string(),
        fulfillmentOptions: shape({
          fulfillable: bool(),
          services: shape({
            type: string(),
            isDefault: bool(),
            deliveryCharge: string(),
            deliveryDates: shape({
              endDate: string(),
              startDate: string()
            }),
            deliveryTimeline: string(),
            freeDeliveryThreshold: string(),
            totalCharge: string(),
            locations: shape({
              type: string(),
              isAnchor: bool(),
              locationId: string(),
              storeName: string(),
              storePhone: string(),
              state: string(),
              distance: string(),
              inventory: shape({
                quantity: string(),
                isInStock: bool(),
                isOutOfStock: bool(),
                isLimitedQuantity: bool(),
                isUnavailable: bool(),
                maxAllowedBopisQty: string(),
                minAllowedBopisQty: string()
              })
            })
          }),
          type: string()
        })
      }
    })
  },
  Buybox
);

BundleBuybox.displayName = 'BundleBuybox';
